<template>
  <div>
    <the-top-bar />
    <c-filter
      :show-filters="true"
    />
    <div class="row no-gutter">
      <div class="col-md-8">
        <c-box-doughnut-chart
          :title="getChartsTitle()"
          :data="charts.data[subject]"
          :loading="fetchingChartsData"
        />
      </div>
      <div class="col-md-4">
        <c-box-button-group />
      </div>
    </div>
    <c-box-table :data="table" />
  </div>
</template>

<script>
import CBoxButtonGroup from '../components/ui/CBoxButtonGroup.vue'
import CBoxDoughnutChart from '../components/ui/CBoxDoughnutChart.vue'
import CBoxTable from '../components/ui/CBoxTable.vue'
import CFilter from '../components/ui/CFilter.vue'
import TheTopBar from '../components/layout/TheTopBar.vue'
import apiService from '../apiService'
import filters from '../mixins/filters'
import helpers from '../mixins/helpers'
import subjects from '../mixins/subjects'
import { Notification } from 'uiv'
import { mapGetters } from 'vuex'
import { capitalizeFirstLetter, round } from '../../../../../../resources/js/helper'

export default {
  name: 'Brief',
  components: {
    CBoxButtonGroup,
    CBoxDoughnutChart,
    CBoxTable,
    CFilter,
    TheTopBar
  },
  mixins: [
    filters,
    helpers,
    subjects
  ],
  data () {
    return {
      charts: {
        data: {
          cpl: [],
          clicks: [],
          conversions: [],
          shows: [],
          costs: []
        }
      },
      table: {},
      fetchingChartsData: false,
      fetchingTableData: false,
      watchEnabled: false
    }
  },
  computed: {
    ...mapGetters({
      filter: 'Filter/getFilterName',
      login: 'Login/getLogin',
      period: 'Period/getPeriod',
      subject: 'Subject/getSubjectName',
      tableData: 'DefaultTableData/getTableData'
    })
  },
  watch: {
    filter (newVal, oldVal) {
      // Отслеживание состояния разрешено только после вызова метода mount(),
      // чтобы избежать лишних запросов с теми же параметрами
      if (this.watchEnabled) {
        if (oldVal && oldVal !== newVal) {
          this.fetchChartsData().then(this.onChartsDataResolve)
        }
      }
    },
    login (newVal, oldVal) {
      if (this.watchEnabled) {
        if (oldVal !== newVal) {
          this.fetchAll()
        }
      }
    },
    period (newVal, oldVal) {
      if (this.watchEnabled) {
        if (oldVal.from !== newVal.from || oldVal.to !== newVal.to) {
          this.fetchAll()
        }
      }
    }
  },
  mounted () {
    // Инициализация данных таблицы
    this.table = this.tableData
    // Подгружаем данные в момент отрисовки страницы и разрешаем отслеживание состояний
    this.fetchAll()
  },
  methods: {
    /**
     * Включает отслеживание состояний
     */
    enableWatch () {
      if (!this.watchEnabled) {
        this.watchEnabled = true
      }
    },
    /**
     * Выполняет запросы к API на получение данных для графика и таблицы
     */
    fetchAll () {
      this.fetchChartsData()
        .then(this.onChartsDataResolve)
        .then(this.enableWatch)
      this.fetchTableData()
        .then(this.onTableDataResolve)
        .then(this.enableWatch)
    },
    /**
     * Метод-обёртка над запросом к API /api/context/brief/charts
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchChartsData () {
      this.fetchingChartsData = true
      return new Promise(resolve => {
        apiService.getBriefChartsData(
          this.period.from,
          this.period.to,
          this.filter,
          this.login
        )
          .then(response => {
            this.fetchingChartsData = false
            resolve(response.data)
          })
          .catch(data => {
            this.fetchingChartsData = false
            Notification.notify.error({
              title: 'Ошибка',
              content: 'Не удалось загрузить диаграмму сводки.'
            })
          })
      })
    },
    /**
     * Метод-обёртка над запросом к API /api/context/brief/table
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchTableData () {
      this.fetchingTableData = true
      return new Promise(resolve => {
        apiService.getBriefTableData(
          this.period.from,
          this.period.to,
          this.login
        )
          .then(response => {
            this.fetchingMainData = false
            resolve(response.data)
          })
          .catch(data => {
            this.fetchingMainData = false
            Notification.notify.error({
              title: 'Ошибка',
              content: 'Не удалось загрузить информацию по сводной таблице.'
            })
          })
      })
    },
    /**
     * Возвращает динамический текст для заголовка графика, который меняется
     * в зависимости от выбранного фильтра или показателя
     * @returns {string}
     */
    getChartsTitle () {
      const subjectText = (this.subject === 'cpl')
        ? this.getSubjectText(this.subject).toUpperCase()
        : capitalizeFirstLetter(this.getSubjectText(this.subject))
      const filterByText = this.getFilterText(this.filter)
      return `${subjectText} ${filterByText}`
    },
    /**
     * Возвращает осмысленный текст по ключу
     * @param name Ключ
     * @returns {*}
     */
    getDeviceText (name) {
      const deviceNames = {
        desktop: 'Десктоп',
        mobile: 'Мобильные',
        smart_tv: 'Смарт ТВ',
        tablet: 'Планшет'
      }
      return deviceNames[name.toLowerCase()]
    },
    /**
     * Метод занимается отрисовкой компонента графика
     * @param data Данные из Promise
     */
    onChartsDataResolve (data) {
      this.charts.data = {
        cpl: [],
        clicks: [],
        conversions: [],
        shows: [],
        costs: []
      }
      for (const prop in data) {
        data[prop].forEach(item => {
          if (!this.charts.data[prop] === undefined) {
            this.charts.data[prop] = []
          }
          this.charts.data[prop].push({
            label: this.filter === 'device' ? this.getDeviceText(item.name) : item.name,
            value: item.value
          })
        })
      }
    },
    /**
     * Метод занимается отрисовкой компонента таблицы
     * @param data Данные из Promise
     */
    onTableDataResolve (data) {
      this.table.rows = []
      this.table.footer = []
      data.rows.forEach(item => {
        this.table.rows.push([
          { text: item.device.name, icon: { className: item.device.icon } },
          { text: item.shows },
          { text: item.clicks },
          { text: round(item.ctr).toFixed(2) },
          { text: round(item.cpc).toFixed(2) },
          { text: round(item.br).toFixed(2) },
          { text: round(item.costs).toFixed(2) },
          { text: item.conversions },
          { text: round(item.cpl).toFixed(2) },
          { text: round(item.cr).toFixed(2) }
        ])
      })
      // Футер
      for (const prop in data.footer) {
        let value = data.footer[prop]
        if (['ctr', 'cpc', 'br', 'costs', 'cpl', 'cr'].includes(prop)) {
          value = round(value).toFixed(2)
        }
        this.table.footer.push({ text: value })
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../../../../resources/css/variables';

.c-filter,
.c-box-button-group,
.c-box-table,
.the-top-bar {
  margin-bottom: 30px;
}
</style>
